import React, { useState } from 'react';
import { Star, ExternalLink } from 'lucide-react';
import { contactInfo } from '../mock';
import Reveal from './Reveal';

const Testimonials = () => {
  return (
    <section id="testimonials" className="bg-[#F7F8FA] py-24">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-2 gap-10">
          {/* Google Reviews CTA card */}
          <Reveal variant="left">
            <div className="text-[#D4A853] tracking-[0.3em] text-xs font-semibold mb-6 text-center">WHAT OUR CLIENTS SAY</div>
            <div className="relative bg-white border border-gray-100 rounded-xl p-8 shadow-sm text-center h-full flex flex-col justify-center">
              {/* Google G logo */}
              <div className="flex justify-center mb-4">
                <div className="w-14 h-14 rounded-full bg-white border border-gray-200 flex items-center justify-center shadow-sm">
                  <svg viewBox="0 0 24 24" width="26" height="26">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                </div>
              </div>

              <div className="flex items-center justify-center gap-2 mb-2">
                <span className="text-4xl font-bold text-[#0B1430]">{contactInfo.googleReviews.rating.toFixed(1)}</span>
                <div className="flex gap-0.5">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} size={18} className="text-[#D4A853] fill-[#D4A853]" />
                  ))}
                </div>
              </div>
              <div className="text-sm text-gray-600 mb-6">
                {contactInfo.googleReviews.count} verified Google reviews
              </div>
              <p className="text-[13px] text-gray-500 leading-relaxed mb-6">
                Our clients love the difference smart lighting makes. Check out what they say — and share your own experience.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href="https://www.google.com/search?q=Future+Smarter+Lighting+Reviews"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-[#0B1430] hover:bg-[#14204A] text-white font-semibold text-[13px] transition-colors"
                >
                  Read Reviews <ExternalLink size={14} />
                </a>
                <a
                  href="https://search.google.com/local/writereview?placeid=ChIJ_wWfjvUXrjsRe8K8X1FbmIk"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md border border-[#D4A853] hover:bg-[#D4A853] text-[#0B1430] font-semibold text-[13px] transition-colors"
                >
                  Leave a Review
                </a>
              </div>
            </div>
          </Reveal>

          {/* Contact form */}
          <Reveal variant="right" delay={200}>
            <ContactForm />
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const ContactForm = () => {
  const [form, setForm] = useState({ name: '', phone: '', email: '', city: '', type: '', message: '' });
  const [sent, setSent] = useState(false);

  const onSubmit = (e) => {
    e.preventDefault();
    setSent(true);
    setTimeout(() => setSent(false), 3000);
    setForm({ name: '', phone: '', email: '', city: '', type: '', message: '' });
  };

  return (
    <div id="contact">
      <div className="text-[#D4A853] tracking-[0.3em] text-xs font-semibold mb-6 text-center">GET IN TOUCH</div>
      <div className="bg-white border border-gray-100 rounded-xl p-6 shadow-sm">
        <div className="text-center font-semibold text-[#0B1430] mb-4">Schedule a Free Home Automation Consultation</div>
        <form onSubmit={onSubmit} className="grid grid-cols-2 gap-3">
          <input className="bl-input" placeholder="Full Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          <input className="bl-input" placeholder="Phone Number" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required />
          <input className="bl-input" placeholder="Email Address" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
          <input className="bl-input" placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
          <select className="bl-input" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
            <option value="">Property Type</option>
            <option>Apartment</option>
            <option>Villa</option>
            <option>Bungalow</option>
            <option>Office</option>
          </select>
          <textarea className="bl-input" placeholder="Message" rows={1} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
          <button type="submit" className="col-span-2 mt-2 px-5 py-3.5 rounded-md bg-[#D4A853] hover:bg-[#B8893A] text-[#0B1430] font-semibold transition-colors">
            {sent ? 'Request Sent ✓' : 'Send Request ➜'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Testimonials;
